var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { stateKey } from '../config/fieldDefinitionTypes';
import { genericFormToProposalData } from './genericFormToProposalData';
import { configRank, instanceOrder, restoreSubfieldOrder } from './subfieldOrder';
/**
 * The rows the save emits for one field instance, in the order it emits them:
 * `instance` alone through {@link genericFormToProposalData}, with `carried` as
 * its passthrough. Empty when the save would drop the instance as blank.
 */
export var emittedInstanceRows = function (fieldDef, instance, carried) {
    var _a, _b;
    var _c;
    if (!instance)
        return [];
    var ticket = (_c = instance._ptId) === null || _c === void 0 ? void 0 : _c.value;
    var config = { tabs: [{ id: 'subfield-rows', fields: [fieldDef] }] };
    return genericFormToProposalData(config, {
        fields: (_a = {}, _a[stateKey(fieldDef)] = [instance], _a),
        passthrough: { unrepresented: [], byInstance: ticket && carried ? (_b = {}, _b[ticket] = carried, _b) : {} },
    });
};
var codeOf = function (row) { var _a; return (_a = row.subfield_code) !== null && _a !== void 0 ? _a : ''; };
/** `key` of the row drawing the `index`-th carried row; stable while the instance stays loaded. */
var carriedKey = function (index) { return "pt".concat(index); };
var buildBase = function (fieldDef, instance, carriedRows, shows) {
    // The instance's own rows in config order: no carried rows, no `_order`.
    var own = emittedInstanceRows(fieldDef, instance && __assign(__assign({}, instance), { _order: undefined }), undefined);
    var items = [];
    var phantom = new Set();
    var drawn = new Map();
    var declared = new Map();
    fieldDef.subfields.forEach(function (sf) {
        if (declared.has(sf.code))
            return;
        declared.set(sf.code, sf);
        var rows = own.filter(function (r) { return codeOf(r) === sf.code; });
        var key = sf.code;
        var visible = shows(sf, rows.length > 0);
        if (visible)
            drawn.set(key, { kind: 'subfield', key: key, code: sf.code, sf: sf });
        rows.forEach(function (stand) { return items.push({ occurrence: { code: sf.code, row: visible ? key : undefined }, stand: stand }); });
        if (rows.length === 0 && visible) {
            // eslint-disable-next-line camelcase -- ProposalDataField uses snake_case to match the API
            var stand = { subfield_code: sf.code };
            phantom.add(stand);
            items.push({ occurrence: { code: sf.code, row: key }, stand: stand });
        }
    });
    // A row the save emitted under a code the config does not declare cannot come from a control.
    own.filter(function (r) { return !declared.has(codeOf(r)); }).forEach(function (stand) { return items.push({ occurrence: { code: codeOf(stand) }, stand: stand }); });
    (carriedRows !== null && carriedRows !== void 0 ? carriedRows : []).forEach(function (row, index) {
        var key = carriedKey(index);
        drawn.set(key, { kind: 'carried', key: key, code: codeOf(row), sf: declared.get(codeOf(row)), row: row, index: index });
        items.push({ occurrence: { code: codeOf(row), row: key }, stand: row });
    });
    return { items: items, carried: new Set(carriedRows !== null && carriedRows !== void 0 ? carriedRows : []), phantom: phantom, rank: configRank(fieldDef), drawn: drawn };
};
var arrange = function (base, order) {
    if (!order)
        return base.items.map(function (i) { return i.occurrence; });
    var byStand = new Map(base.items.map(function (i) { return [i.stand, i.occurrence]; }));
    return restoreSubfieldOrder(base.items.map(function (i) { return i.stand; }), order, base.rank, base.carried, base.phantom)
        .map(function (stand) { return byStand.get(stand); });
};
var rowsIn = function (occurrences, drawn) {
    var seen = new Set();
    var rows = [];
    occurrences.forEach(function (_a) {
        var row = _a.row;
        if (row === undefined || seen.has(row))
            return;
        seen.add(row);
        rows.push(drawn.get(row));
    });
    return rows;
};
/**
 * The rows one field instance draws, in order, and what moving one of them
 * writes.
 *
 * <p>`shows(sf, filled)` says whether a declared subfield gets a row at all —
 * the panel's call (hidden subfields, `visibleWhen`, collapsed optional ones).
 * A multi-value subfield (CZNA-1378) is one control holding every value, so it
 * is one row, drawn where its first value stands, and it moves whole.
 */
export var subfieldRowLayout = function (fieldDef, instance, carried, shows) {
    var base = buildBase(fieldDef, instance, carried, shows);
    var occurrences = arrange(base, instanceOrder(instance));
    var rows = rowsIn(occurrences, base.drawn);
    var keys = rows.map(function (r) { return r.key; });
    /** `_order` with the occurrences of row `key` put before (`after` false) or after `neighbour`. */
    var reordered = function (key, neighbour, after, expected) {
        // `_order` holds one character per occurrence.
        if (occurrences.some(function (o) { return o.code.length !== 1; }))
            return undefined;
        var moving = occurrences.filter(function (o) { return o.row === key; });
        var rest = occurrences.filter(function (o) { return o.row !== key; });
        var at = after
            ? rest.map(function (o) { return o.row; }).lastIndexOf(neighbour) + 1
            : rest.findIndex(function (o) { return o.row === neighbour; });
        var order = __spreadArray(__spreadArray(__spreadArray([], rest.slice(0, at), true), moving, true), rest.slice(at), true).map(function (o) { return o.code; });
        var laidOut = rowsIn(arrange(base, order), base.drawn).map(function (r) { return r.key; });
        return laidOut.join('\u0000') === expected.join('\u0000') ? order.join('') : undefined;
    };
    var moved = function (key, delta) {
        var _a;
        var from = keys.indexOf(key);
        var to = from + delta;
        if (from < 0 || to < 0 || to >= keys.length)
            return undefined;
        var expected = __spreadArray([], keys, true);
        _a = [expected[to], expected[from]], expected[from] = _a[0], expected[to] = _a[1];
        return reordered(key, keys[to], delta > 0, expected);
    };
    var placedAfter = function (key, anchor) {
        if (key === anchor || !keys.includes(key) || !keys.includes(anchor))
            return undefined;
        var expected = keys.filter(function (k) { return k !== key; });
        expected.splice(expected.indexOf(anchor) + 1, 0, key);
        return reordered(key, anchor, true, expected);
    };
    return { rows: rows, occurrences: occurrences, moved: moved, placedAfter: placedAfter };
};
/**
 * CZNA-1515 — what removing the row `key` of `layout` writes to `_order`, or
 * `undefined` when the stored order cannot keep every other row where it
 * stands. `next`, `nextCarried` and `shows` are the instance as it will be once
 * the row is gone; `order` is absent when the instance needs no `_order` at all.
 *
 * <p>The new order is the old one without the row's occurrences, checked the
 * way a move is: by laying the rows out again and comparing with the rows as
 * they stand now, minus the removed one. A carried row is compared as the row
 * itself, since its key is its index and shifts once an earlier row goes.
 */
export var orderWithout = function (fieldDef, layout, key, next, nextCarried, shows) {
    var expected = layout.rows.filter(function (r) { return r.key !== key; });
    if (expected.length === layout.rows.length)
        return undefined;
    var same = function (a, b) { return (a.kind === 'carried'
        ? b.kind === 'carried' && a.row === b.row
        : b.kind === 'subfield' && a.key === b.key); };
    var fits = function (order) {
        var rows = subfieldRowLayout(fieldDef, __assign(__assign({}, next), { _order: order === undefined ? undefined : { value: order } }), nextCarried, shows).rows;
        return rows.length === expected.length && rows.every(function (r, i) { return same(expected[i], r); });
    };
    if (!instanceOrder(next) && fits(undefined))
        return {};
    var occurrences = layout.occurrences.filter(function (o) { return o.row !== key; });
    // `_order` holds one character per occurrence.
    if (occurrences.some(function (o) { return o.code.length !== 1; }))
        return undefined;
    var order = occurrences.map(function (o) { return o.code; }).join('');
    return fits(order) ? { order: order } : undefined;
};
